import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { institutionProductFilterConfig } from '@adornis/bh-buildify/client/nodes/institution-product-filter/config.js';
import { institutionProductsConfig } from '@adornis/bh-buildify/client/nodes/institution-products/config.js';
import { getRegistrationColorDefinitionsForInstitution } from '@adornis/bh-buildify/client/nodes/institution-products/helper/productCardShared.js';
import {
  counselingCardDataContext,
  counselingCardParts,
  type CounselingCardData,
} from '@adornis/bh-buildify/client/nodes/institution-products/helper/renderCounselingCard.js';
import {
  workshopCardDataContext,
  workshopCardParts,
  type WorkshopCardData,
} from '@adornis/bh-buildify/client/nodes/institution-products/helper/renderWorkshopCard.js';
import {
  workshopSetCardDataContext,
  workshopSetCardParts,
  type WorkshopSetCardData,
} from '@adornis/bh-buildify/client/nodes/institution-products/helper/renderWorkshopSetCard.js';
import {
  counselingCardBuildifyPreset,
  productListingPageBuildifyPreset,
  workshopCardBuildifyPreset,
  workshopSetCardBuildifyPreset,
} from '@adornis/buchungsheld-db/_buildify-presets.js';
import { RegistrationColorDefinitions } from '@adornis/buchungsheld-db/entities/ColorDefinitions.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { SettingColorDefintionProp } from '@adornis/buchungsheld-db/entities/settings.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { WorkshopSet } from '@adornis/buchungsheld-db/entities/WorkshopSet.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { BuildifyLitElement } from '@adornis/html-based-buildify/client/BuildifyLitElement.js';
import { fullEditorConfig } from '@adornis/html-based-buildify/client/editor/presets/full-editor.js';
import { runInsertNode } from '@adornis/html-based-buildify/client/helper/runInsertNode.js';
import { MenuItemGroup, type INodeConfig } from '@adornis/html-based-buildify/client/types.js';
import { isNodeSelection } from '@adornis/html-based-buildify/client/util.js';
import type { HTMLBase } from '@adornis/html-based-buildify/db/HTMLBase.js';
import { consume, provide } from '@lit/context';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { BehaviorSubject, combineLatest, filter, map, switchMap, tap } from 'rxjs';
import { bhBuldifyEditor, bhBuldifyEditorActions } from '../../../../components/bh-buildify-editor.js';
import { EinrichtungSettingsAdapter } from './EinrichtungSettingsAdapter.js';

export const cardPartConfig = (
  cardType: 'workshop' | 'counseling' | 'workshop-set',
  part: keyof typeof workshopCardParts,
): INodeConfig => {
  const name = `${cardType}_card_part_${part}`;
  const tag = `node-${cardType}-card-part`;
  return {
    node: {
      name,
      schema: {
        draggable: true,
        group: 'block',
        attrs: { 'data-part': { default: part } },
        parseDOM: [
          {
            tag,
            getAttrs: node => ({ 'data-part': node.getAttribute('data-part') }),
          },
        ],
        toDOM: node => {
          return [
            tag,
            {
              'data-part': node.attrs['data-part'],
              'no-content': 'true',
            },
          ];
        },
      },
    },
    menuItems: [
      {
        group: MenuItemGroup.MEDIA,
        label: 'Filter',
        render: editorView => {
          return html`<x-flex
            horizontal
            space="xxs"
            crossaxis-center
            ${css({ color: 'blue', fontSize: '18px', cursor: 'pointer' })}
          >
            <x-icon>title</x-icon> <x-text>${part}</x-text>
          </x-flex>`;
        },
        run: view => runInsertNode(name)(view.state, view),
        shouldVisualize: view => {
          return !isNodeSelection(view.state.selection);
        },
      },
    ],
  };
};

@customElement('node-workshop-set-card-part')
class NodeWorkshopSetCardPart extends ChemistryLitElement {
  @consume({ context: workshopSetCardDataContext, subscribe: true })
  @state()
  workshopSetCardData!: WorkshopSetCardData;

  @property({ attribute: 'data-part' })
  partName!: string;

  override render() {
    if (!this.partName) return nothing;
    return html`${this.getRenderFunction()(this.workshopSetCardData)}`;
  }

  getRenderFunction() {
    if (!workshopSetCardParts.hasOwnProperty(this.partName))
      throw new Error('no render function registered for part: ' + this.partName);
    return workshopSetCardParts[this.partName];
  }

  override styles() {
    // TODO: deactiavte all pointer events when in preview-mode
    return [...super.styles()];
  }
}

@customElement('node-workshop-card-part')
class NodeWorkshopCardAppointmentButton extends ChemistryLitElement {
  @consume({ context: workshopCardDataContext, subscribe: true })
  @state()
  workshopCardData!: WorkshopCardData;

  @property({ attribute: 'data-part' })
  partName!: string;

  override render() {
    if (!this.partName) return nothing;
    return html`${this.getRenderFunction()(this.workshopCardData)}`;
  }

  getRenderFunction() {
    if (!workshopCardParts.hasOwnProperty(this.partName))
      throw new Error('no render function registered for part: ' + this.partName);
    return workshopCardParts[this.partName];
  }

  override styles() {
    // TODO: deactiavte all pointer events when in preview-mode
    return [...super.styles()];
  }
}

@customElement('node-counseling-card-part')
class NodeCounselingCardAppointmentButton extends ChemistryLitElement {
  @consume({ context: counselingCardDataContext, subscribe: true })
  @state()
  counselingCardData!: CounselingCardData;

  @property({ attribute: 'data-part' })
  partName!: string;

  override render() {
    if (!this.partName) return nothing;
    return html`${this.getRenderFunction()(this.counselingCardData)}`;
  }

  getRenderFunction() {
    if (!counselingCardParts.hasOwnProperty(this.partName))
      throw new Error('no render function registered for part: ' + this.partName);
    return counselingCardParts[this.partName];
  }

  override styles() {
    // TODO: deactiavte all pointer events when in preview-mode
    return [...super.styles()];
  }
}

export const ProductNameConfig: INodeConfig = {
  node: {
    name: 'product-name',
    schema: {
      draggable: true,
      group: 'inline',
      inline: true,
      parseDOM: [
        {
          tag: 'node-product-name',
          getAttrs: node => ({}),
        },
      ],
      toDOM: node => {
        return [
          'node-product-name',
          {
            ['no-content']: 'true',
          },
        ];
      },
    },
  },
  menuItems: [
    {
      group: MenuItemGroup.HEADINGS,
      label: 'Name',
      render: editorView => {
        return html`<x-flex
          horizontal
          space="xxs"
          crossaxis-center
          ${css({ color: 'red', fontSize: '18px', cursor: 'pointer' })}
        >
          <x-icon>title</x-icon> <x-text>Name</x-text>
        </x-flex>`;
      },
      run: view => runInsertNode('product-name')(view.state, view),
      shouldVisualize: view => {
        return !isNodeSelection(view.state.selection);
      },
    },
  ],
};

@customElement('node-product-name')
export class NodeWorkshopProperty extends BuildifyLitElement<HTMLBase> {
  @consume({ context: workshopCardDataContext })
  @state()
  workshopCardData!: WorkshopCardData;

  @consume({ context: counselingCardDataContext })
  @state()
  counselingCardData?: CounselingCardData;

  @consume({ context: workshopSetCardDataContext })
  @state()
  workshopSetCardData?: WorkshopSetCardData;

  override createRenderRoot() {
    return this;
  }

  override content({ content, mode }) {
    return html`${this.workshopCardData?.workshop.name ??
    this.counselingCardData?.counseling.name ??
    this.workshopSetCardData?.workshopSet.name ??
    ''}`;
  }
}

const modes = ['listing', 'workshop-card', 'counseling-card', 'workshop-set-card'] as const;
type Mode = (typeof modes)[number];

/**
 * @element bh-page-admin-einrichtung-settings-product-page
 */
@customElement('bh-page-admin-einrichtung-settings-product-page')
export class BhPageAdminEinrichtungSettingsProductPage extends EinrichtungSettingsAdapter {
  @state() private readonly viewMode = new BehaviorSubject<Mode>('listing');

  @state() private readonly institution = new RXController(
    this,
    this.institutionID.observable.pipe(
      filter(isDefined),
      switchMap(institutionID =>
        subscribeByID(
          Institution,
          institutionID,
        )({
          _id: 1,

          settings: {
            colorDefinitions: { registrationColorDefinitions: RegistrationColorDefinitions.allFields },
            inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
          },
        }),
      ),
      filter(isDefined),
    ),
  );

  @state() private readonly workshopCardTestData = new RXController(
    this,
    combineLatest([DesignSystem.currentTheme, this.institution.observable]).pipe(
      map(([theme, institution]) => {
        return {
          workshop: new Workshop({
            name: 'Test',
            institutionID: institution!._id,
            translatedDescriptionHTML: [
              { langKey: 'de', text: 'Beispiel Beschreibung' },
              { langKey: 'en', text: 'Example description' },
            ],
            appointments: [
              { start: DateTime.now().plus({ days: 2 }), end: DateTime.now().plus({ days: 2, hours: 2 }) },
              { start: DateTime.now().plus({ days: 3 }), end: DateTime.now().plus({ days: 3, hours: 2 }) },
            ],
            settings: {
              courseLocation: 'Test',
            },
          }),
          start: DateTime.now().plus({ days: 2 }),
          end: DateTime.now().plus({ days: 3 }),
          colorDef: getRegistrationColorDefinitionsForInstitution(institution!),
          selected: false,
          bookedAlready: false,
          isClickable: true,
          isBlockedByOtherSelection: false,
          price: 1499,
        };
      }),
      tap(workshopCardTestData => (this.workshopCardData = workshopCardTestData)),
    ),
  );

  // needed to get workshopCardComponents preview in buildify-editor
  @provide({ context: workshopCardDataContext })
  @state()
  private workshopCardData?: WorkshopCardData;

  @state() private readonly counselingCardTestData = new RXController(
    this,
    combineLatest([DesignSystem.currentTheme, this.institution.observable]).pipe(
      map(([theme, institution]) => {
        return {
          counseling: new Einzelberatung({
            name: 'Test',
            institutionID: institution!._id,
            translatedDescriptionHTML: [
              { langKey: 'de', text: 'Beispiel Beschreibung' },
              { langKey: 'en', text: 'Example description' },
            ],
            slotConfig: {
              start: DateTime.now(),
              end: DateTime.now().plus({ hours: 1 }),
            },
          }),
          start: DateTime.now().plus({ days: 2 }),
          end: DateTime.now().plus({ days: 2, hours: 6 }),
          colorDef: getRegistrationColorDefinitionsForInstitution(institution!),
          slotData: [
            {
              timeslot: { start: DateTime.now(), end: DateTime.now().plus({ hours: 1 }), isBooked: false },
              isSelected: true,
              hasTimeCollision: false,
            },
            {
              timeslot: {
                start: DateTime.now().plus({ hours: 1 }),
                end: DateTime.now().plus({ hours: 2 }),
                isBooked: false,
              },
              isSelected: false,
              hasTimeCollision: false,
            },
            {
              timeslot: {
                start: DateTime.now().plus({ hours: 2 }),
                end: DateTime.now().plus({ hours: 3 }),
                isBooked: false,
              },
              isSelected: false,
              hasTimeCollision: false,
            },
          ],
          selected: false,
          bookedAlready: false,
          isClickable: true,
          isBlockedByOtherSelection: false,
          price: 1099,
        };
      }),
      tap(counselingCardTestData => (this.counselingCardData = counselingCardTestData)),
    ),
  );

  // needed to get counselingCardComponents preview in buildify-editor
  @provide({ context: counselingCardDataContext })
  @state()
  private counselingCardData?: CounselingCardData;

  @state() private readonly workshopSetTestData = new RXController(
    this,
    combineLatest([DesignSystem.currentTheme, this.institution.observable]).pipe(
      map(([theme, institution]) => {
        return {
          workshopSet: new WorkshopSet({
            name: 'Test Workshop Set',
            institutionID: institution!._id,
            translatedDescriptionHTML: [
              { langKey: 'de', text: 'Beispiel Workshop Reihe Beschreibung' },
              { langKey: 'en', text: 'Example workshop set description' },
            ],
            categoryID: '',
            category: { name: 'Test Category' },
            workshops: [
              {
                _id: '1',
                name: 'Workshop 1',
                institutionID: institution!._id,
                appointments: [{ start: DateTime.now(), end: DateTime.now().plus({ hours: 1 }) }],
              },
              {
                _id: '2',
                name: 'Workshop 2',
                institutionID: institution!._id,
                appointments: [{ start: DateTime.now(), end: DateTime.now().plus({ hours: 1 }) }],
              },
            ],
          }),
          colorDef: getRegistrationColorDefinitionsForInstitution(institution!),
          selected: false,
          bookedAlready: false,
          isClickable: true,
          isBlockedByOtherSelection: false,
          price: 2499,
        };
      }),
      tap(workshopSetTestData => (this.workshopSetCardData = workshopSetTestData)),
    ),
  );

  @provide({ context: workshopSetCardDataContext })
  @state()
  private workshopSetCardData?: WorkshopSetCardData;

  override render() {
    const institutionController = this._form;
    const institution = institutionController.document;
    const institutionID = this.institutionID.value;
    const workshopCardTestData = this.workshopCardTestData.value;
    const counselingCardTestData = this.counselingCardTestData.value;
    const workshopSetTestData = this.workshopSetTestData.value;
    const viewMode = this.viewMode.value;
    if (
      !institution ||
      !institutionController ||
      !institutionController.document ||
      !institutionID ||
      !viewMode ||
      !workshopCardTestData ||
      !counselingCardTestData ||
      !workshopSetTestData
    )
      return html` <x-loader></x-loader> `;

    const content = (() => {
      if (viewMode === 'listing') return this.listingEditor(institution);
      if (viewMode === 'workshop-card') return this.workshopCardEditor(institution, workshopCardTestData);
      if (viewMode === 'counseling-card') return this.counselingCardEditor(institution, counselingCardTestData);
      if (viewMode === 'workshop-set-card') return this.workshopSetCardEditor(institution, workshopSetTestData);
      throw new Error('type ' + viewMode + ' not implemented');
    })();

    const actions = (() => {
      if (viewMode === 'listing')
        return bhBuldifyEditorActions(this, institution, 'productPage', productListingPageBuildifyPreset);
      if (viewMode === 'workshop-card')
        return bhBuldifyEditorActions(this, institution, 'productPageWorkshopCard', workshopCardBuildifyPreset);
      if (viewMode === 'counseling-card')
        return bhBuldifyEditorActions(this, institution, 'productPageCounselingCard', counselingCardBuildifyPreset);
      if (viewMode === 'workshop-set-card')
        return bhBuldifyEditorActions(this, institution, 'productPageWorkshopSetCard', workshopSetCardBuildifyPreset);
      throw new Error('type ' + viewMode + ' not implemented');
    })();

    return this.renderContent(actions, content);
  }

  renderContent(actions, content) {
    for (const action of actions) this.exposeAction(action.icon, action);

    return html`
      <x-flex space="xl">
        <x-tabs .selected=${this.viewMode.value}>
          ${modes.map(
            mode =>
              html`
                <x-tab label=${mode} @click=${() => (this.viewMode.next(mode), this.requestUpdate())}>
                  ${this._translation.translate('PRODUCT', 'designEdit', mode)}
                </x-tab>
              `,
          )}
        </x-tabs>

        ${content}
      </x-flex>
    `;
  }

  listingEditor(institution: Institution) {
    return html`
      <x-flex space="md">
        ${bhBuldifyEditor(institution, 'productPage', {
          prosemirrorConfig: [...fullEditorConfig(), institutionProductFilterConfig, institutionProductsConfig],
          breakpoints: {
            mobile: 450,
            tablet: 700,
            desktop: 1200,
          },
          defaultDesign: productListingPageBuildifyPreset,
        })}
      </x-flex>
    `;
  }

  workshopCardEditor(institution: Institution, workshopCardData: WorkshopCardData) {
    return html`
      <x-flex space="md">
        ${bhBuldifyEditor(institution, 'productPageWorkshopCard', {
          prosemirrorConfig: [
            ...fullEditorConfig(),
            ProductNameConfig,
            ...Object.keys(workshopCardParts).map(part => cardPartConfig('workshop', part)),
          ],
          breakpoints: {
            mobile: 450,
            tablet: 700,
            desktop: 1200,
          },
          defaultDesign: workshopCardBuildifyPreset,
        })}
      </x-flex>
    `;
  }

  counselingCardEditor(institution: Institution, counselingCardData: CounselingCardData) {
    return html`
      <x-flex space="md">
        ${bhBuldifyEditor(institution, 'productPageCounselingCard', {
          prosemirrorConfig: [
            ...fullEditorConfig(),
            ProductNameConfig,
            ...Object.keys(counselingCardParts).map(part => cardPartConfig('counseling', part)),
          ],
          breakpoints: {
            mobile: 450,
            tablet: 700,
            desktop: 1200,
          },
          defaultDesign: counselingCardBuildifyPreset,
        })}
      </x-flex>
    `;
  }

  workshopSetCardEditor(institution: Institution, workshopSetCardData: WorkshopSetCardData) {
    return html`
      <x-flex space="md">
        ${bhBuldifyEditor(institution, 'productPageWorkshopSetCard', {
          prosemirrorConfig: [
            ...fullEditorConfig(),
            ProductNameConfig,
            ...Object.keys(workshopSetCardParts).map(part => cardPartConfig('workshop-set', part)),
          ],
          breakpoints: {
            mobile: 450,
            tablet: 700,
            desktop: 1200,
          },
          defaultDesign: workshopSetCardBuildifyPreset,
        })}
      </x-flex>
    `;
  }
}
